import React, { useState, useRef } from 'react';
import { Camera, X, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { PhotoTipsModal } from '../PhotoTipsModal';
import { Card } from '../ui/Card';
interface ProblemDescriptionProps {
  description: string;
  photos: string[];
  isBlocking: boolean | null;
  onDescriptionChange: (text: string) => void;
  onPhotosChange: (photos: string[]) => void;
  onIsBlockingChange: (isBlocking: boolean) => void;
}
export const ProblemDescription: React.FC<ProblemDescriptionProps> = ({
  description,
  photos,
  isBlocking,
  onDescriptionChange,
  onPhotosChange,
  onIsBlockingChange
}) => {
  const {
    t
  } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isTipsModalOpen, setIsTipsModalOpen] = useState(false);
  const descriptionLength = description.length;
  const isDescriptionValid = descriptionLength >= 20 && descriptionLength <= 1000;
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = event => {
      if (event.target?.result) {
        const newPhoto = event.target.result as string;
        onPhotosChange([...photos, newPhoto]);
      }
    };
    reader.readAsDataURL(file);
  };
  const handleAddPhotoClick = () => {
    setIsTipsModalOpen(true);
  };
  const handleOpenCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleRemovePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
  };
  return <div data-id="element-503">
      <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-504">
        {t.problemDescription.title}
      </h2>
      <p className="text-gray-600 mb-8" data-id="element-505">{t.problemDescription.subtitle}</p>

      <div className="space-y-8" data-id="element-506">
        {/* Photo Upload - MOVED TO TOP */}
        <div data-id="element-507">
          <div className="flex items-center justify-between mb-4" data-id="element-508">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2" data-id="element-509">
              {t.problemDescription.labels.photos}
              <span className="text-red-500" data-id="element-510">*</span>
            </label>
            <span className="text-sm text-gray-500 font-medium" data-id="element-511">
              {t.problemDescription.labels.photosCount.replace('{count}', photos.length.toString())}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-4" data-id="element-512">
            {photos.map((photo, index) => <div key={index} className="relative" data-id="element-513">
                <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-32 object-cover rounded-lg shadow-sm" data-id="element-514" />
                <button onClick={() => handleRemovePhoto(index)} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md" data-id="element-515">
                  <X className="w-4 h-4" data-id="element-516" />
                </button>
              </div>)}

            {photos.length < 3 && <button onClick={handleAddPhotoClick} className={`w-full h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-all group ${photos.length === 0 ? 'border-red-300 text-red-400 hover:border-red-400 hover:text-red-500 hover:bg-red-50' : 'border-gray-300 text-gray-400 hover:border-teal-deep hover:text-teal-deep hover:bg-teal-50'}`} data-id="element-517">
                <Camera className="w-10 h-10 mb-2 group-hover:scale-110 transition-transform" data-id="element-518" />
                <span className="text-sm font-medium" data-id="element-519">
                  {t.problemDescription.buttons.addPhoto}
                </span>
              </button>}
          </div>
          {photos.length === 0 && <p className="text-red-500 text-sm mt-2" data-id="element-520">
              {t.problemDescription.validation.photoRequired}
            </p>}
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" data-id="element-521" />
        </div>

        {/* Urgency Assessment - MOVED TO MIDDLE */}
        <div data-id="element-522">
          <div className="flex items-center gap-2 mb-4" data-id="element-523">
            <h3 className="text-sm font-medium text-gray-700" data-id="element-524">
              {t.problemDescription.urgency.question}
            </h3>
            <span className="text-red-500" data-id="element-525">*</span>
          </div>
          <Card className={`p-6 transition-all ${isBlocking !== null ? 'ring-2 ring-teal-deep bg-teal-50/30' : 'border border-gray-200'}`} data-id="element-526">
            <div className="flex items-start gap-4" data-id="element-527">
              <AlertCircle className={`w-6 h-6 flex-shrink-0 mt-1 ${isBlocking !== null ? 'text-teal-deep' : 'text-gray-400'}`} data-id="element-528" />
              <div className="flex-1" data-id="element-529">
                <div className="space-y-3" data-id="element-530">
                  <label className="flex items-center gap-3 cursor-pointer group" data-id="element-531">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isBlocking === true ? 'border-red-500 bg-red-500' : 'border-gray-300 group-hover:border-red-400'}`} data-id="element-532">
                      {isBlocking === true && <div className="w-2.5 h-2.5 bg-white rounded-full" data-id="element-533" />}
                    </div>
                    <input type="radio" checked={isBlocking === true} onChange={() => onIsBlockingChange(true)} className="hidden" data-id="element-534" />
                    <span className={`text-lg ${isBlocking === true ? 'text-red-700 font-medium' : 'text-gray-700'}`} data-id="element-535">
                      {t.problemDescription.urgency.yes}
                    </span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer group" data-id="element-536">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isBlocking === false ? 'border-teal-deep bg-teal-deep' : 'border-gray-300 group-hover:border-teal-400'}`} data-id="element-537">
                      {isBlocking === false && <div className="w-2.5 h-2.5 bg-white rounded-full" data-id="element-538" />}
                    </div>
                    <input type="radio" checked={isBlocking === false} onChange={() => onIsBlockingChange(false)} className="hidden" data-id="element-539" />
                    <span className={`text-lg ${isBlocking === false ? 'text-teal-900 font-medium' : 'text-gray-700'}`} data-id="element-540">
                      {t.problemDescription.urgency.no}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </Card>
          {isBlocking === null && <p className="text-red-500 text-sm mt-2" data-id="element-541">
              {t.problemDescription.validation.urgencyRequired}
            </p>}
        </div>

        {/* Description Textarea - MOVED TO BOTTOM */}
        <div data-id="element-542">
          <div className="flex items-center justify-between mb-2" data-id="element-543">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2" data-id="element-544">
              {t.problemDescription.labels.description}
              <span className="text-red-500" data-id="element-545">*</span>
            </label>
            <span className={`text-sm font-medium ${descriptionLength < 20 ? 'text-red-500' : descriptionLength > 1000 ? 'text-red-500' : 'text-gray-500'}`} data-id="element-546">
              {t.problemDescription.characterCount.replace('{current}', descriptionLength.toString()).replace('{max}', '1000')}
            </span>
          </div>
          <textarea value={description} onChange={e => onDescriptionChange(e.target.value)} placeholder={t.problemDescription.placeholders.description} maxLength={1000} className={`w-full min-h-[200px] px-4 py-3 text-lg border rounded-lg focus:ring-2 focus:ring-teal-deep focus:border-transparent resize-none transition-colors ${description.length > 0 && !isDescriptionValid ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`} data-id="element-547" />
          {description.length > 0 && descriptionLength < 20 && <p className="text-red-500 text-sm mt-2" data-id="element-548">
              {t.problemDescription.validation.descriptionTooShort}
            </p>}
          {descriptionLength > 1000 && <p className="text-red-500 text-sm mt-2" data-id="element-549">
              {t.problemDescription.validation.descriptionTooLong}
            </p>}
        </div>
      </div>

      <PhotoTipsModal isOpen={isTipsModalOpen} onClose={() => setIsTipsModalOpen(false)} onOpenCamera={handleOpenCamera} data-id="element-550" />
    </div>;
};